import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { MapEntity } from "@mapmycustomers/shared/types/entity";
import { EntityPin } from "@mapmycustomers/shared/types/map";

import EntityAnnotation from "@app/component/EntityAnnotation";
import MarkerEffect from "@app/enum/MarkerEffect";
import AnnotationData from "@app/scene/dashboard/components/cards/NoContactIn/CardContentComponent/types/AnnotationData";
import OutOfFrequencyCardData from "@app/scene/dashboard/store/cards/outOfFrequency/OutOfFrequencyCardData";
import { showEntityView } from "@app/store/entityView/actions";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";

import EntityItem from "./EntityItem";
import styles from "./ListContent.module.scss";

const LOAD_MORE_THRESHOLD = 100; // px

interface Props {
  data?: OutOfFrequencyCardData;
  fullWidth?: boolean;
  onLoadMore?: () => void;
  onShowPreviewPane: typeof showEntityView;
  total?: number;
}

const ListContent: React.FC<Props> = ({
  data,
  fullWidth,
  onLoadMore,
  onShowPreviewPane,
  total,
}) => {
  const getPinStyle = useMemo(
    () =>
      defaultEntityStyleGetter({
        effect: MarkerEffect.NONE,
        markerSize: MarkerSize.LARGE,
        shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
        showEntityIcon: true,
      }),
    []
  );

  const getEntityIcon = useCallback(
    (entity: MapEntity) => {
      // this is a dirty dirty hack, my bad
      // but changing defaultEntityStyleGetter to support normal records instead of EntityPins
      // requires extra work, but what's worse, I don't have a straight idea of how to
      // do that exactly. Because, strictly speaking defaultEntityStyleGetter should accept
      // EntityPins. Probably there must be some style getter that accepts entities and
      // defaultEntityStyleGetter will wrap it.
      // FIXME: address that issue later ^^^
      const styles = getPinStyle({ data: entity } as unknown as EntityPin);
      return styles?.icon ? (styles.icon as google.maps.Icon).url : undefined;
    },
    [getPinStyle]
  );

  const handleScroll = useCallback(
    ({ target }) => {
      const { clientHeight, scrollHeight, scrollTop } = target;
      if (
        scrollHeight - clientHeight - scrollTop < LOAD_MORE_THRESHOLD &&
        (data?.entities?.length ?? 0) < (total ?? 0)
      ) {
        onLoadMore?.();
      }
    },
    [data?.entities, total, onLoadMore]
  );

  const [annotation, setAnnotation] = useState<AnnotationData | undefined>();
  const handleHideAnnotation = useCallback(() => setAnnotation(undefined), []);

  return (
    <div className={styles.container} onScroll={handleScroll}>
      {(data?.entities || []).map((entity) => (
        <EntityItem
          entity={entity}
          fullWidth={fullWidth}
          iconGetter={getEntityIcon}
          key={entity.id}
          onShowAnnotation={setAnnotation}
          onShowPreviewPane={onShowPreviewPane}
        />
      ))}
      {annotation && (
        <EntityAnnotation
          coordinates={annotation.coordinates}
          entityId={annotation.entityId}
          entityType={annotation.entityType}
          onHide={handleHideAnnotation}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onShowPreviewPane: showEntityView,
};

export default connect(null, mapDispatchToProps)(ListContent);
